import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tooltip as UITooltip, TooltipContent, TooltipTrigger, TooltipProvider } from './ui/tooltip';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Area, Line, ComposedChart } from 'recharts';
import { Loader2, Info, TrendingUp } from 'lucide-react';

export const MASKED_BRL = 'R$ •••••';
const brlRaw = (v) => (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const pctStr = (p) => (p === null || p === undefined ? '—' : `${p > 0 ? '+' : ''}${p.toFixed(1).replace('.', ',')}%`);
const pctClass = (p) => (p === null || p === undefined ? 'text-muted-foreground' : p >= 0 ? 'text-green-600' : 'text-red-600');

const PAGO_HELP = 'Valor dos pedidos com pagamento confirmado na loja. Pode diferir do valor já depositado na sua conta, por causa dos prazos de repasse do meio de pagamento.';

export const PagoInfo = () => (
  <TooltipProvider><UITooltip><TooltipTrigger asChild>
    <Info className="h-3 w-3 cursor-help text-emerald-600/70" data-testid="pago-info-icon" />
  </TooltipTrigger><TooltipContent className="max-w-xs"><p>{PAGO_HELP}</p></TooltipContent></UITooltip></TooltipProvider>
);

const Title = ({ subtitle }) => (
  <CardHeader>
    <CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4 text-indigo-500" />Projeção</CardTitle>
    {subtitle && <p className="text-xs text-muted-foreground" data-testid="projecao-subtitle">{subtitle}</p>}
  </CardHeader>
);

// Pure view: receives projection data by props (undefined = loading, null = error). Never fetches.
export const SalesProjectionView = ({ data: p, privado = false, subtitle }) => {
  const brl = privado ? () => MASKED_BRL : brlRaw;

  if (p === undefined) return <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-indigo-600" /></div>;
  if (!p || !p.available) {
    return (
      <Card data-testid="projecao-section"><Title subtitle={subtitle} />
        <CardContent><p className="text-sm text-muted-foreground" data-testid="projecao-unavailable">{(p && p.reason) || 'Histórico insuficiente para projeção'}</p></CardContent>
      </Card>
    );
  }

  const b = p.bruto, pago = p.pago, ly = p.ano_passado, nm = p.proximo_mes;
  return (
    <Card data-testid="projecao-section">
      <Title subtitle={subtitle} />
      <CardContent className="space-y-5">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-lg border p-4 bg-indigo-50/50" data-testid="projecao-fechamento">
            <p className="text-sm text-muted-foreground">O mês deve fechar em</p>
            <p className="text-2xl font-bold text-indigo-700" data-testid="projecao-provavel">{brl(b.probable)}</p>
            <p className="text-xs text-emerald-700 mt-0.5 flex items-center gap-1">Pago projetado: <span className="font-medium">{brl(pago.probable)}</span><PagoInfo /></p>
            <p className="text-xs text-muted-foreground mt-1">Faixa: entre {brl(b.conservative)} e {brl(b.optimistic)}</p>
            <p className="text-xs text-emerald-700/90">Pago: entre {brl(pago.conservative)} e {brl(pago.optimistic)}</p>
            {ly?.disponivel && ly.delta_pct !== null && (
              <p className={`text-xs mt-1 font-medium ${pctClass(ly.delta_pct)}`}>{pctStr(ly.delta_pct)} vs. mesmo mês do ano passado</p>
            )}
          </div>
          <div className="rounded-lg border p-4" data-testid="projecao-proximo-mes">
            <p className="text-sm text-muted-foreground">Estimativa do próximo mês</p>
            {nm?.available ? (
              <>
                <p className="text-2xl font-bold">{brl(nm.valor)}</p>
                <p className="text-xs text-emerald-700 mt-0.5 flex items-center gap-1">Pago projetado: <span className="font-medium">{brl(nm.valor_pago)}</span><PagoInfo /></p>
                <p className="text-xs text-muted-foreground mt-1">Baseado no mesmo mês do ano passado, ajustado pelo crescimento recente.</p>
              </>
            ) : (
              <p className="text-sm text-muted-foreground mt-2">{nm?.reason || 'Histórico insuficiente para estimar o próximo mês.'}</p>
            )}
          </div>
        </div>

        <div style={{ width: '100%', height: 300 }} data-testid="projecao-chart">
          <ResponsiveContainer>
            <ComposedChart data={p.chart} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="dia" tick={{ fontSize: 11 }} />
              {privado
                ? <YAxis tick={false} width={10} />
                : <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />}
              {privado
                ? <Tooltip formatter={(v, name) => ['', name]} separator="" labelFormatter={(d) => `Dia ${d}`} />
                : <Tooltip formatter={(v) => (v === null ? '-' : brl(v))} labelFormatter={(d) => `Dia ${d}`} />}
              <Legend />
              <Area type="monotone" dataKey="faixa_max" name="Faixa" stroke="none" fill="#6366f1" fillOpacity={0.12} />
              <Area type="monotone" dataKey="faixa_min" name=" " stroke="none" fill="#ffffff" fillOpacity={1} legendType="none" />
              <Line type="monotone" dataKey="realizado" name="Realizado" stroke="#6366f1" strokeWidth={2.5} dot={false} connectNulls />
              <Line type="monotone" dataKey="projecao" name="Projeção" stroke="#6366f1" strokeWidth={2} strokeDasharray="5 4" dot={false} connectNulls />
              <Line type="monotone" dataKey="ano_passado" name="Ano passado" stroke="#94a3b8" strokeWidth={1.5} dot={false} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <p className="text-xs text-muted-foreground border-t pt-3">Projeção estatística baseada no histórico de vendas. Não considera campanhas, promoções ou eventos que não ocorreram no período usado como base.</p>
      </CardContent>
    </Card>
  );
};
